import React, { useEffect, useState, useMemo } from "react";
import { Bell, AlertCircle, Clock, CheckCheck, ExternalLink, ChevronRight, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications } from "@/hooks/useNotifications";
import { useDespachos, Process } from "@/hooks/useDespachos";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Resolves the destination URL and judicial details for any notification item.
 * Prioritizes deep-linking directly to `/dashboard/despachos/procesos/:id` when the
 * notification pertains to a court case/expediente.
 */
export const resolveNotificationDestination = (
  notification: any,
  processes: Process[] = []
): { url: string; radicado?: string; isCase: boolean } => {
  if (!notification) return { url: "/dashboard/despachos", isCase: false };

  // 1. Explicit action_url if already provided
  const explicitUrl =
    notification.action_url ||
    notification.metadata?.action_url ||
    notification.payload?.action_url;
  if (explicitUrl && typeof explicitUrl === "string" && explicitUrl.trim()) {
    const trimmed = explicitUrl.trim();
    return {
      url: trimmed,
      isCase: trimmed.includes("/despachos/procesos"),
    };
  }

  // 2. Direct radicado or case/process IDs from attributes or metadata/payload
  const rawId =
    notification.numero_proceso ||
    notification.radicado ||
    notification.process_id ||
    notification.case_id ||
    notification.metadata?.numero_proceso ||
    notification.metadata?.radicado ||
    notification.metadata?.process_id ||
    notification.metadata?.case_id ||
    notification.payload?.numero_proceso ||
    notification.payload?.radicado ||
    notification.payload?.process_id ||
    notification.payload?.case_id ||
    notification.data?.numero_proceso ||
    notification.data?.radicado ||
    notification.data?.process_id ||
    notification.data?.case_id;

  if (rawId && typeof rawId === "string" && rawId.trim()) {
    const cleanId = rawId.trim();
    const cleanDigits = cleanId.replace(/\D/g, "");

    // Try finding in processes list
    const matched = processes.find(
      (p) =>
        p.id === cleanId ||
        p.case_number === cleanId ||
        p.radicado === cleanId ||
        (cleanDigits.length >= 8 &&
          ((p.case_number && p.case_number.replace(/\D/g, "") === cleanDigits) ||
            (p.radicado && p.radicado.replace(/\D/g, "") === cleanDigits)))
    );

    const target = matched ? (matched.id || matched.case_number || cleanId) : cleanId;
    return {
      url: `/dashboard/despachos/procesos/${target}`,
      radicado: matched?.case_number || matched?.radicado || (cleanDigits.length >= 10 ? cleanId : undefined),
      isCase: true,
    };
  }

  // 3. Scan title, message, description, content for 21-23 digit radicado
  const textBlob = `${notification.title || ""} ${notification.message || ""} ${notification.description || ""} ${notification.content || ""}`;

  // Continuous 21-23 digits (Colombian judicial radicado standard)
  const digitMatch = textBlob.match(/\b(\d{21,23})\b/);
  if (digitMatch && digitMatch[1]) {
    const foundRad = digitMatch[1];
    const matched = processes.find(
      (p) =>
        (p.case_number && p.case_number.replace(/\D/g, "") === foundRad) ||
        (p.radicado && p.radicado.replace(/\D/g, "") === foundRad)
    );
    const target = matched ? (matched.id || matched.case_number || foundRad) : foundRad;
    return {
      url: `/dashboard/despachos/procesos/${target}`,
      radicado: foundRad,
      isCase: true,
    };
  }

  // Hyphenated radicado format (e.g. 11001-40-03-001-2023-00123-00)
  const hyphenMatch = textBlob.match(/\b(\d{5}-\d{2}-\d{2}-\d{3}-\d{4}-\d{5}-\d{2})\b/);
  if (hyphenMatch && hyphenMatch[1]) {
    const cleanRad = hyphenMatch[1].replace(/-/g, "");
    return {
      url: `/dashboard/despachos/procesos/${cleanRad}`,
      radicado: cleanRad,
      isCase: true,
    };
  }

  // 4. Try matching against any loaded process in workspace
  for (const proc of processes) {
    if (proc.case_number && proc.case_number.length >= 8 && textBlob.includes(proc.case_number)) {
      return {
        url: `/dashboard/despachos/procesos/${proc.id || proc.case_number}`,
        radicado: proc.case_number,
        isCase: true,
      };
    }
    if (proc.radicado && proc.radicado.length >= 8 && textBlob.includes(proc.radicado)) {
      return {
        url: `/dashboard/despachos/procesos/${proc.id || proc.radicado}`,
        radicado: proc.radicado,
        isCase: true,
      };
    }
  }

  // 5. Fallback if it is an explicit case/court update but without identifiable ID
  const isCaseActivity =
    notification.activity_type === "case_update" ||
    notification.type === "case_update" ||
    notification.type === "update" ||
    notification.type === "actuacion" ||
    notification.type === "proceso" ||
    /actuaci[oó]n|proceso|radicado|juzgado|despacho|expediente/i.test(textBlob);

  if (isCaseActivity) {
    return {
      url: "/dashboard/despachos/procesos",
      isCase: true,
    };
  }

  // 6. Generic other module routes
  if (notification.activity_type === "document_generation") return { url: "/dashboard/documents", isCase: false };
  if (notification.activity_type === "contract_review") return { url: "/dashboard/contract-review", isCase: false };
  if (notification.activity_type === "consultation") return { url: "/dashboard/legal-chatbot", isCase: false };
  if (notification.activity_type === "search") return { url: "/dashboard/legal-research", isCase: false };

  return { url: "/dashboard/despachos", isCase: false };
};

export const NotificationBell: React.FC = () => {
  const { notifications, markAsRead, markAllAsRead, refresh } = useNotifications();
  const { processes } = useDespachos();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const unreadNotifications = useMemo(() => notifications.filter((n) => !n.read), [notifications]);
  const unreadCount = unreadNotifications.length;

  // WebSocket real-time listener connection to Legal Scrapper backend
  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimeout: any = null;
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 1;

    const getSecureWsUrl = () => {
      let rawUrl = import.meta.env.VITE_WS_URL || "ws://localhost:8082/api/ws/notifications";
      if (typeof window !== "undefined" && window.location.protocol === "https:") {
        if (rawUrl.startsWith("ws://")) {
          rawUrl = rawUrl.replace(/^ws:\/\//i, "wss://");
        }
      }
      return rawUrl;
    };

    const connectWebSocket = () => {
      if (!isMounted) return;
      if (retryCount >= MAX_RETRIES) {
        console.info("[WS FRONTEND] WebSocket retry limit reached. Realtime active via Supabase.");
        return;
      }

      const targetUrl = getSecureWsUrl();

      // Guard: Do not attempt unencrypted ws:// on HTTPS pages
      if (typeof window !== "undefined" && window.location.protocol === "https:" && targetUrl.startsWith("ws://")) {
        console.warn("[WS FRONTEND] Skipping insecure ws:// connection on HTTPS page.");
        return;
      }

      try {
        ws = new WebSocket(targetUrl);

        ws.onopen = () => {
          retryCount = 0;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "NEW_ACTUACION") {
              const destination = resolveNotificationDestination(data, processes);

              // Trigger Toast Notification immediately
              toast({
                title: `🔔 Nueva Actuación Judicial`,
                description: `${data.message || 'Se registró un nuevo movimiento en tu proceso.'}`,
                action: (
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-legal-blue text-white border-none hover:bg-legal-navy text-xs"
                    onClick={() => navigate(destination.url)}
                  >
                    Ver Proceso
                  </Button>
                ),
              });
              // Refresh notifications from Supabase
              refresh();
            }
          } catch (err) {
            console.error("[WS FRONTEND ERROR] Failed to parse message:", err);
          }
        };

        ws.onerror = () => {
          // Silent handling on error
        };

        ws.onclose = () => {
          if (isMounted) {
            retryCount++;
            if (retryCount < MAX_RETRIES) {
              const backoff = Math.min(5000 * Math.pow(1.5, retryCount), 20000);
              reconnectTimeout = setTimeout(connectWebSocket, backoff);
            }
          }
        };
      } catch (err: any) {
        retryCount++;
      }
    };

    connectWebSocket();

    return () => {
      isMounted = false;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [processes]);

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "d MMM, h:mm a", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const handleSelectNotification = (notification: any) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    const { url } = resolveNotificationDestination(notification, processes);
    setIsOpen(false);
    navigate(url);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-xl hover:bg-gray-100/80 group transition-all"
          title="Notificaciones y Alertas Judiciales"
        >
          <Bell className="h-5 w-5 text-slate-600 group-hover:text-legal-blue transition-colors" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-white text-[10px] font-extrabold items-center justify-center border-2 border-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[360px] sm:w-[420px] p-0 rounded-2xl shadow-2xl border-gray-100 z-50 overflow-hidden"
        align="end"
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-blue-50/50">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-legal-blue" />
            <h3 className="font-bold text-sm text-gray-900">
              Notificaciones
            </h3>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 hover:bg-red-600 text-white text-[10px] px-2 py-0.5 border-none rounded-full">
                {unreadCount} nuevas
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead()}
              className="text-[11px] text-legal-blue hover:text-legal-navy hover:bg-blue-50 h-7 px-2 font-medium"
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Marcar leídas
            </Button>
          )}
        </div>

        {/* List of Notifications */}
        <ScrollArea className="h-[360px] max-h-[60vh]">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300 stroke-[1.5]" />
              <p className="text-xs font-medium">No tienes notificaciones pendientes</p>
              <p className="text-[11px] text-gray-400 mt-1">
                Las alertas de movimientos judiciales aparecerán aquí en tiempo real.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {notifications.map((notification: any) => {
                const isUnread = !notification.read;
                const { url: destUrl, radicado: detectedRadicado, isCase } = resolveNotificationDestination(
                  notification,
                  processes
                );
                const radicado =
                  notification.numero_proceso ||
                  notification.metadata?.numero_proceso ||
                  notification.payload?.numero_proceso ||
                  detectedRadicado;

                return (
                  <div
                    key={notification.id}
                    className={`p-3.5 hover:bg-blue-50/40 transition-colors cursor-pointer group flex items-start gap-3 ${
                      isUnread ? "bg-blue-50/30" : ""
                    }`}
                    onClick={() => handleSelectNotification(notification)}
                  >
                    {/* Status Dot */}
                    <div className="mt-1 shrink-0">
                      {isUnread ? (
                        <div className="w-2.5 h-2.5 rounded-full bg-legal-blue ring-4 ring-blue-100" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-gray-300" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className={`text-xs font-bold truncate ${isUnread ? "text-gray-900" : "text-gray-700"}`}>
                          {notification.title || (isCase ? "Actualización Procesal" : "Notificación")}
                        </p>
                        <span className="text-[10px] text-gray-400 flex items-center shrink-0">
                          <Clock className="h-3 w-3 mr-1 text-gray-300" />
                          {formatDate(notification.created_at)}
                        </span>
                      </div>

                      <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed">
                        {notification.message || notification.description || notification.content}
                      </p>

                      {/* Footer tags & quick link */}
                      {isCase ? (
                        <div className="mt-2.5 pt-1.5 border-t border-slate-100 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0 truncate">
                            <Briefcase className="h-3 w-3 text-slate-400 shrink-0" />
                            {radicado ? (
                              <Badge
                                variant="outline"
                                className="text-[9px] bg-slate-50 text-slate-700 border-slate-200 font-mono py-0 h-4 truncate"
                              >
                                Rad: {radicado}
                              </Badge>
                            ) : (
                              <span className="text-[10px] text-slate-500 font-medium truncate">
                                Expediente Despachos
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-legal-blue font-semibold flex items-center gap-0.5 group-hover:text-blue-700 transition-colors shrink-0">
                            Ver expediente
                            <ChevronRight className="h-3 w-3" />
                          </span>
                        </div>
                      ) : (
                        destUrl && destUrl !== "/dashboard/despachos" && (
                          <div className="mt-2 pt-1 border-t border-slate-100/60 flex items-center justify-end">
                            <span className="text-[10px] text-slate-500 group-hover:text-legal-blue font-medium flex items-center gap-0.5 transition-colors">
                              Ir al detalle
                              <ExternalLink className="h-2.5 w-2.5" />
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-2.5 border-t border-gray-100 bg-gray-50/50 text-center">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-slate-600 hover:text-legal-blue hover:bg-white h-8 font-medium justify-center"
            onClick={() => {
              setIsOpen(false);
              navigate("/dashboard/notifications");
            }}
          >
            Ver historial completo
            <ExternalLink className="h-3 w-3 ml-1.5" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
